import { useTranslation } from "react-i18next";

import AvailabilityModeRadioGroup from "../../components/AvailabilityModeRadioGroup";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppDialog from "../../design-system/components/AppDialog";
import AppDialogBody from "../../design-system/components/AppDialogBody";
import AppDialogFooter from "../../design-system/components/AppDialogFooter";
import AppDialogHeader from "../../design-system/components/AppDialogHeader";
import AppField from "../../design-system/components/AppField";
import AppFieldGrid from "../../design-system/components/AppFieldGrid";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppText from "../../design-system/components/AppText";
import AppTextarea from "../../design-system/components/AppTextarea";
import AppTooltip from "../../design-system/components/AppTooltip";
import {
  IconAdd,
  IconDelete,
  IconSettings,
} from "../../design-system/icons";
import AvailabilityDateField from "./AvailabilityDateField";
import AvailabilitySectionTitle from "./AvailabilitySectionTitle";
import AvailabilitySwitchSection from "./AvailabilitySwitchSection";

export default function AvailabilitySettingsDialog({
  open,
  draft,
  setDraft,
  onClose,
  onSave,
  saving,
  saveDisabled = false,
}) {
  const { t } = useTranslation();

  const updateCompany = (field, value) => {
    setDraft((current) => ({
      ...current,
      company: { ...current.company, [field]: value },
    }));
  };

  const addFreezePeriod = () => {
    const today = new Date();
    const date = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, "0"),
      String(today.getDate()).padStart(2, "0"),
    ].join("-");
    setDraft((current) => ({
      ...current,
      freeze_periods: [
        ...(current.freeze_periods || []),
        { id: "", name: "", start_date: date, end_date: date, comment: "" },
      ],
    }));
  };

  const updateFreezePeriod = (index, field, value) => {
    setDraft((current) => ({
      ...current,
      freeze_periods: (current.freeze_periods || []).map((period, periodIndex) =>
        periodIndex === index ? { ...period, [field]: value } : period
      ),
    }));
  };

  const removeFreezePeriod = (index) => {
    setDraft((current) => ({
      ...current,
      freeze_periods: (current.freeze_periods || []).filter((_, periodIndex) => periodIndex !== index),
    }));
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="lg"
      className="availability-settings-dialog"
    >
      <AppDialogHeader
        title={`${t("availability.settings_dialog.title")}${draft.location_name ? ` — ${draft.location_name}` : ""}`}
        visual={<IconSettings />}
      />
      <AppDialogBody>
        <AppFieldStack size="sm">
          <section>
            <AvailabilitySectionTitle
              title={t("availability.settings_dialog.defaults.title")}
              info={t("availability.settings_dialog.defaults.info")}
              infoLabel={t("availability.settings_dialog.defaults.info_aria")}
            />
            <AvailabilityModeRadioGroup
              value={draft.company.availability_mode}
              selectionCards
              onChange={(event) => updateCompany("availability_mode", event.target.value)}
            />
          </section>

          <div className="availability-switch-section-group">
            <AvailabilitySwitchSection
              title={t("availability.settings_dialog.approval.title")}
              description={t("settings_ui.entries_require_approval_info")}
              checked={Boolean(draft.company.approval_required)}
              onChange={(event) => updateCompany("approval_required", event.target.checked)}
              aria-label={t("availability.settings_dialog.approval.title")}
            />
            <AvailabilitySwitchSection
              title={t("availability.settings_dialog.deadline.title")}
              description={t("availability.settings_dialog.deadline.info")}
              checked={Boolean(draft.company.edit_lock_enabled)}
              onChange={(event) => updateCompany("edit_lock_enabled", event.target.checked)}
              aria-label={t("availability.settings_dialog.deadline.title")}
            />
            {draft.company.edit_lock_enabled ? (
              <AppFieldStack
                size="md"
                style={{ paddingTop: "var(--ds-field-layout-row-md)" }}
              >
                <AppFieldGrid columns={2} size="md">
                  <AppField
                    label={t("availability.settings_dialog.deadline.cutoff_day")}
                    help={t("availability.settings_dialog.deadline.cutoff_day_helper")}
                    required
                  >
                    <AppInput
                      type="number"
                      value={draft.company.edit_lock_day_of_month}
                      min={1}
                      max={31}
                      step={1}
                      onChange={(event) => updateCompany("edit_lock_day_of_month", event.target.value)}
                    />
                  </AppField>
                  <AppField
                    label={t("availability.settings_dialog.deadline.locked_months")}
                    help={t("availability.settings_dialog.deadline.locked_months_helper")}
                    required
                  >
                    <AppInput
                      type="number"
                      value={draft.company.edit_lock_horizon_months}
                      min={0}
                      step={1}
                      onChange={(event) => updateCompany("edit_lock_horizon_months", event.target.value)}
                    />
                  </AppField>
                </AppFieldGrid>
                <AppCallout
                  variant="info"
                  description={t("availability.settings_dialog.deadline.example")}
                />
              </AppFieldStack>
            ) : null}
          </div>

          <section>
            <AvailabilitySectionTitle
              title={t("availability.settings_dialog.freeze_periods.title")}
              info={t("availability.settings_dialog.freeze_periods.info")}
              infoLabel={t("availability.settings_dialog.freeze_periods.info_aria")}
              action={(
                <AppButton variant="neutral" size="sm" onClick={addFreezePeriod} disabled={saving}>
                  <IconAdd />
                  {t("availability.settings_dialog.freeze_periods.add")}
                </AppButton>
              )}
            />
            {(draft.freeze_periods || []).length ? (
              <AppFieldStack size="md">
                {(draft.freeze_periods || []).map((period, index) => (
                  <AppFieldGrid columns={4} size="md" key={period.id || `freeze-period-${index}`}>
                    <AppField
                      label={t("availability.settings_dialog.freeze_periods.name")}
                      required
                      span={2}
                    >
                      <AppInput
                        value={period.name || ""}
                        onChange={(event) => updateFreezePeriod(index, "name", event.target.value)}
                      />
                    </AppField>
                    <AvailabilityDateField
                      label={t("availability.settings_dialog.freeze_periods.from")}
                      required
                      value={period.start_date || ""}
                      onChange={(value) => updateFreezePeriod(index, "start_date", value)}
                    />
                    <AvailabilityDateField
                      label={t("availability.settings_dialog.freeze_periods.until")}
                      required
                      value={period.end_date || ""}
                      onChange={(value) => updateFreezePeriod(index, "end_date", value)}
                    />
                    <AppField label={t("availability.settings_dialog.freeze_periods.comment")} span={3}>
                      <AppTextarea
                        rows={1}
                        value={period.comment || ""}
                        onChange={(event) => updateFreezePeriod(index, "comment", event.target.value)}
                      />
                    </AppField>
                    <AppField label={t("availability.settings_dialog.freeze_periods.remove")}>
                      <AppTooltip content={t("availability.settings_dialog.freeze_periods.remove")}>
                        <AppButton
                          variant="error"
                          size="md"
                          iconOnly
                          onClick={() => removeFreezePeriod(index)}
                          disabled={saving}
                          aria-label={t("availability.settings_dialog.freeze_periods.remove")}
                        >
                          <IconDelete />
                        </AppButton>
                      </AppTooltip>
                    </AppField>
                  </AppFieldGrid>
                ))}
              </AppFieldStack>
            ) : (
              <AppText variant="body-2" tone="secondary">
                {t("availability.settings_dialog.freeze_periods.empty")}
              </AppText>
            )}
          </section>
        </AppFieldStack>
      </AppDialogBody>
      <AppDialogFooter>
        <AppButton variant="neutral" size="md" onClick={onClose} disabled={saving}>
          {t("availability.settings_dialog.cancel")}
        </AppButton>
        <AppButton variant="primary" size="md" onClick={onSave} disabled={saving || saveDisabled}>
          {t("availability.settings_dialog.save")}
        </AppButton>
      </AppDialogFooter>
    </AppDialog>
  );
}
